/** Matches a leading URL scheme (`https:`, `javascript:`, …) per RFC 3986. */
const HAS_SCHEME = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

/**
 * Normalize a URL typed by a human before storing it. Producers write
 * `www.venue.com/induction` far more often than they write the scheme, and
 * validating that as-is would silently delete the field on the next save, so
 * a scheme-less value is assumed to be https.
 *
 * Anything that already carries a scheme is passed through untouched — so
 * `javascript:alert(1)` is still rejected rather than becoming a "safe"
 * `https://javascript:alert(1)`.
 */
export function normalizeStoredUrl(url: string | null | undefined): string | undefined {
  if (typeof url !== 'string') return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  // Protocol-relative `//host` stays rejected — it is not a typo for https.
  const candidate =
    HAS_SCHEME.test(trimmed) || trimmed.startsWith('//') ? trimmed : `https://${trimmed}`;
  return safeExternalHref(candidate);
}

/** Return a normalized external URL only for explicitly allowed schemes.
 * Relative, protocol-relative, malformed, and executable URLs are rejected. */
export function safeExternalHref(url: string | null | undefined): string | undefined {
  if (typeof url !== 'string') return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:' && parsed.protocol !== 'mailto:') {
      return undefined;
    }
    return parsed.href;
  } catch {
    return undefined;
  }
}
